import axios from 'axios';

// Determine environment
const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// API base URL
const API_BASE_URL = isDevelopment
  ? 'http://127.0.0.1:8000/api/'
  : 'https://api.prakriya.chilipili.in/api/';

// Media URL (without /api/ suffix)
export const MEDIA_URL = isDevelopment
  ? 'http://127.0.0.1:8000'
  : 'https://api.prakriya.chilipili.in';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // Increased timeout
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true // Important for cookies if used
});

// Token management
const getTokens = () => ({
  access: localStorage.getItem('accessToken'),
  refresh: localStorage.getItem('refreshToken')
});

const setTokens = (access, refresh) => {
  localStorage.setItem('accessToken', access);
  localStorage.setItem('refreshToken', refresh);
};

const clearTokens = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
};

// Improved refresh token function
const refreshAccessToken = async () => {
  try {
    const refresh = localStorage.getItem('refreshToken');
    if (!refresh) throw new Error('No refresh token available');

    // Use a direct axios instance to avoid interceptors loop
    const response = await axios.post(`${API_BASE_URL}auth/token/refresh/`, {
      refresh: refresh
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (response.data.access) {
      localStorage.setItem('accessToken', response.data.access);
      return response.data.access;
    }
    throw new Error('Refresh token request did not return an access token');
  } catch (error) {
    console.error('Token refresh failed:', error);
    clearTokens();
    throw error;
  }
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    
    // If the request data is FormData, remove Content-Type header
    // to let the browser set it with the correct boundary
    if (config.data instanceof FormData) {
      console.log('FormData detected, removing Content-Type header');
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor with improved error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only attempt to refresh if we have a 401/403 and haven't tried already
    if (error.response && [401, 403].includes(error.response.status) && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        console.log('Token expired, attempting to refresh...');
        const newAccessToken = await refreshAccessToken();
        console.log('Token refreshed successfully');
        
        // Update the header for the retry
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        
        // Create a new instance of axios to avoid interceptor loops
        return axios(originalRequest);
      } catch (refreshError) {
        console.error('Unable to refresh token:', refreshError);
        clearTokens();
        
        // Redirect to login only if we're not already there
        if (!window.location.pathname.includes('/login')) {
          console.log('Redirecting to login page');
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Error handler utility with improved logging
export const handleApiError = (error) => {
  if (error.response) {
    console.error('API Error Response:', error.response.status, error.response.data);
    return error.response.data.detail || error.response.data.message || 'An error occurred';
  }
  console.error('API Error:', error.message);
  return error.message || 'Network error occurred';
};

export { getTokens, setTokens, clearTokens };
export default api;